// useWhatsNewEntryAutoOpen — 入室直後に「新機能の紹介」を 1 回だけ自動で開く判断。
// (docs/_wip/whats-new-2026-10-15.md「初回表示の条件」)
//
// RoomLayout (Classic ⇄ Modern 切替で remount しない) が呼ぶ。返す `autoOpen` が true の間、
// アクティブなレイアウトが useWhatsNew({autoOpen}) 経由でダイアログを開き、onAutoOpened で消費する。
//
// 順序 (入室時に出すものの取り合い):
//   1. お知らせの入室モーダルが先。AnnounceEntryModal は mount から ENTRY_WINDOW_MS の
//      受付ウィンドウに届いた warning/critical を出す。ウィンドウが閉じるまで判断を待ち
//      (+ 余裕 SLACK_MS)、閉じた時点で出す/出した/出す予定のものがあれば、今回は出さない
//      (既読にもしない = 次の入室で出る)。
//   2. レイヤー非表示の確認ダイアログ・過去キャンバスの深リンクが開いている間は待つ
//      (深リンクは今回出さない)。
//   2b. 判断の時点までに本人が描き始めていたら (stroke.start 済み、描画中を含む) 出さない。
//   3. 判断は 1 入室につき 1 回 (`gate.decided`)。RootSwitch が持つので RoomLayout が
//      remount / 再接続しても二度開かない。手動の入室・退室でクリアされる。

import { useCallback, useEffect, useRef, useState } from 'react';

import { getOwnStrokeStartedCount } from '../../application/ownStrokeActivity.js';
import { hasUnreadWhatsNew, type WhatsNewEntry } from '../../domain/whatsNew.js';
import type { VisitorKind } from '../../infrastructure/returningVisitor.js';
import type { WhatsNewSeenStore } from '../../infrastructure/whatsNewSeen.js';
import { ENTRY_WINDOW_MS } from '../AnnounceEntryModal.js';

/** 受付ウィンドウが閉じた直後の最終 reconcile を待つ余裕。 */
export const WHATS_NEW_ENTRY_SLACK_MS = 150;

export interface WhatsNewEntryGate {
  decided: boolean;
}

export interface UseWhatsNewEntryAutoOpenInput {
  readonly visitor: VisitorKind;
  readonly entries: ReadonlyArray<WhatsNewEntry>;
  readonly store: WhatsNewSeenStore;
  readonly gate: WhatsNewEntryGate;
  /** お知らせの入室モーダルが出た / 出ている / 出る予定。 */
  readonly announceBusy: boolean;
  /** 他の入室時ダイアログが開いている (レイヤー非表示の確認など)。閉じるまで待つ。 */
  readonly otherModalOpen: boolean;
  /** 過去キャンバスの深リンクで入室した (今回は出さない)。 */
  readonly skipThisEntry: boolean;
  /** 自分が stroke.start を送った通算回数 (既定は ownStrokeActivity)。テストで注入。 */
  readonly getStrokeStartedCount?: () => number;
  readonly windowMs?: number;
}

export interface WhatsNewEntryAutoOpen {
  readonly autoOpen: boolean;
  readonly onAutoOpened: () => void;
}

export const useWhatsNewEntryAutoOpen = (
  input: UseWhatsNewEntryAutoOpenInput,
): WhatsNewEntryAutoOpen => {
  const { visitor, entries, store, gate, announceBusy, otherModalOpen, skipThisEntry } = input;
  const windowMs = input.windowMs ?? ENTRY_WINDOW_MS + WHATS_NEW_ENTRY_SLACK_MS;
  const getCount = input.getStrokeStartedCount ?? getOwnStrokeStartedCount;
  // mount 時点の回数。ここから増えていれば「入室後に描き始めた」。
  const baselineRef = useRef<number | null>(null);
  if (baselineRef.current === null) baselineRef.current = getCount();
  const [windowClosed, setWindowClosed] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);

  useEffect(() => {
    if (gate.decided) return;
    const t = setTimeout(() => setWindowClosed(true), windowMs);
    return () => clearTimeout(t);
  }, [gate, windowMs]);

  useEffect(() => {
    if (gate.decided || !windowClosed) return;
    // 初めての人 / storage 不可 / 深リンク入室 / お知らせ優先: 今回は出さない (判断は確定)。
    // 入室後に 1 本でも描き始めた (描画中を含む) なら出さない。既読にもしない (次の入室で出る)。
    const startedDrawing = getCount() > (baselineRef.current ?? 0);
    if (visitor !== 'returning' || skipThisEntry || announceBusy || startedDrawing) {
      gate.decided = true;
      return;
    }
    if (otherModalOpen) return; // 閉じるまで待つ。
    gate.decided = true;
    if (hasUnreadWhatsNew(entries, store.readSeenIds())) setAutoOpen(true);
  }, [
    gate,
    windowClosed,
    visitor,
    skipThisEntry,
    announceBusy,
    otherModalOpen,
    entries,
    store,
    getCount,
  ]);

  const onAutoOpened = useCallback(() => setAutoOpen(false), []);
  return { autoOpen, onAutoOpened };
};
